import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message, toast } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import {
  FailedNotificationTasksPage,
  isRetryable,
} from 'api/highnote/notificationTasks'
import { useFailedNotificationTasks } from 'queries/admin/highnote/useFailedNotificationTasks'
import { useRetryNotificationTaskMutation } from 'queries/admin/highnote/useRetryNotificationTaskMutation'
import { useFiltersQueryState } from '../hooks/useFiltersQueryState'
import { useSelectedColumnsQueryState } from '../hooks/useSelectedColumnsQueryState'
import { SelectableColumns } from '../components/NotificationTasksTable/ColumnsFilter/columns'

const PAGE_SIZE = 50

type NotificationTasksContextState = {
  state: {
    data: FailedNotificationTasksPage
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pageIndex: number
    canGoPrevious: boolean
    canGoNext: boolean
    filters: {
      id: number | null
      status: string[]
      eventName: string[]
      search: string
    }
    selectedColumnsFilters: SelectableColumns[]
    selectedIds: Set<number>
    retryableIds: number[]
    modalPayload: string | null
    isRetrying: boolean
  }
  actions: {
    refetch: () => void
    goToPreviousPage: () => void
    goToNextPage: () => void
    setFilters: (
      update: Partial<{
        id: number | null
        status: string[]
        eventName: string[]
        search: string
      }>
    ) => void
    setSelectedColumnsFilters: ReturnType<
      typeof useSelectedColumnsQueryState
    >[1]
    toggleRow: (id: number) => void
    toggleSelectAll: () => void
    handleRetrySelected: () => Promise<void>
    setModalPayload: (payload: string | null) => void
  }
}

const [Context, useNotificationTasksContextValue] =
  createContext<NotificationTasksContextState>({
    name: 'NotificationTasksContext',
  })

export const NotificationTasksProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters, setFilters] = useFiltersQueryState()
  const [selectedColumnsFilters, setSelectedColumnsFilters] =
    useSelectedColumnsQueryState()

  const filterSignature = JSON.stringify({
    id: filters.id,
    status: filters.status,
    eventName: filters.eventName,
    search: filters.search,
  })

  const [pageState, setPageState] = useState<{
    signature: string
    cursors: Array<number | undefined>
    pageIndex: number
  }>({ signature: filterSignature, cursors: [undefined], pageIndex: 0 })

  const pageCursors =
    pageState.signature === filterSignature ? pageState.cursors : [undefined]
  const pageIndex =
    pageState.signature === filterSignature ? pageState.pageIndex : 0

  useEffect(() => {
    if (pageState.signature !== filterSignature) {
      setPageState({
        signature: filterSignature,
        cursors: [undefined],
        pageIndex: 0,
      })
    }
  }, [filterSignature, pageState.signature])

  const setFiltersAndResetPage = (
    update: Partial<{
      id: number | null
      status: string[]
      eventName: string[]
      search: string
    }>
  ) => {
    setFilters(update)
  }

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [modalPayload, setModalPayload] = useState<string | null>(null)

  const cursor = pageCursors[pageIndex]

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useFailedNotificationTasks({
    pageSize: PAGE_SIZE,
    cursor,
    id: filters.id,
    status: filters.status,
    eventName: filters.eventName,
    search: filters.search,
  })

  const { mutateAsync: retryTask, isPending: isRetrying } =
    useRetryNotificationTaskMutation()

  const events = data?.events ?? []

  const retryableIds = useMemo(
    () => events.filter(isRetryable).map((event) => event.id),
    [events]
  )

  const toggleRow = useCallback((id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  const toggleSelectAll = useCallback(() => {
    setSelectedIds((prev) => {
      if (retryableIds.every((id) => prev.has(id))) {
        return new Set()
      }
      return new Set(retryableIds)
    })
  }, [retryableIds])

  const handleRetrySelected = useCallback(async () => {
    const ids = Array.from(selectedIds)
    if (ids.length === 0) return

    const results = await Promise.allSettled(ids.map((id) => retryTask(id)))
    const succeeded = results.filter((r) => r.status === 'fulfilled').length
    const failed = results.length - succeeded

    if (succeeded > 0) {
      toast.success(`Retried ${succeeded} task${succeeded === 1 ? '' : 's'}`)
    }
    if (failed > 0) {
      toast.error(`Failed to retry ${failed} task${failed === 1 ? '' : 's'}`)
    }

    setSelectedIds(new Set())
  }, [retryTask, selectedIds])

  const goToNextPage = useCallback(() => {
    if (!data?.nextCursor) return
    const nextPageIndex = pageIndex + 1
    setPageState((current) => {
      if (current.signature !== filterSignature) {
        return current
      }
      const nextCursors = current.cursors.slice(0, nextPageIndex)
      nextCursors[nextPageIndex] = data.nextCursor ?? undefined
      return {
        signature: filterSignature,
        cursors: nextCursors,
        pageIndex: nextPageIndex,
      }
    })
  }, [data?.nextCursor, filterSignature, pageIndex])

  const goToPreviousPage = useCallback(() => {
    if (pageIndex === 0) return
    setPageState((current) => {
      if (current.signature !== filterSignature) {
        return current
      }
      return {
        ...current,
        pageIndex: current.pageIndex - 1,
      }
    })
  }, [filterSignature, pageIndex])

  if (isPending || !data) {
    return <Loading />
  }

  if (isError) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  const value: NotificationTasksContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pageIndex: pageIndex + 1,
      canGoPrevious: pageIndex > 0,
      canGoNext: data.nextCursor !== null,
      filters,
      selectedColumnsFilters,
      selectedIds,
      retryableIds,
      modalPayload,
      isRetrying,
    },
    actions: {
      refetch,
      goToPreviousPage,
      goToNextPage,
      setFilters: setFiltersAndResetPage,
      setSelectedColumnsFilters,
      toggleRow,
      toggleSelectAll,
      handleRetrySelected,
      setModalPayload,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useNotificationTasksContextValue }
